/**
 * LayoutContext.tsx
 *
 * React Context for managing EMS layout state.
 * Provides shared state for the AdminRail collapsed state and mobile navigation.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';

// ============================================================================
// Types
// ============================================================================

export interface LayoutContextValue {
  /** Whether the AdminRail is collapsed (desktop only) */
  isRailCollapsed: boolean;
  /** Set the rail collapsed state */
  setRailCollapsed: (collapsed: boolean) => void;
  /** Toggle the rail collapsed state */
  toggleRailCollapsed: () => void;
  /** Whether the mobile navigation drawer is open */
  isMobileNavOpen: boolean;
  /** Set the mobile navigation open state */
  setMobileNavOpen: (open: boolean) => void;
  /** Open the mobile navigation */
  openMobileNav: () => void;
  /** Close the mobile navigation */
  closeMobileNav: () => void;
  /** Current active module (for module-based navigation) */
  activeModule: string | null;
  /** Set the active module */
  setActiveModule: (module: string | null) => void;
}

// ============================================================================
// Constants
// ============================================================================

const COLLAPSED_STORAGE_KEY = 'ems-adminrail-collapsed';

// ============================================================================
// Context
// ============================================================================

const LayoutContext = createContext<LayoutContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export interface LayoutProviderProps {
  children: ReactNode;
  defaultCollapsed?: boolean;
}

export function LayoutProvider({ children, defaultCollapsed }: LayoutProviderProps) {
  // Initialize rail collapsed state from localStorage
  const [isRailCollapsed, setIsRailCollapsed] = useState(() => {
    if (defaultCollapsed !== undefined) return defaultCollapsed;
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    return stored === 'true';
  });

  // Mobile navigation state
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Active module state
  const [activeModule, setActiveModule] = useState<string | null>(null);

  // Persist rail collapsed state
  useEffect(() => {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, String(isRailCollapsed));
  }, [isRailCollapsed]);

  // Close mobile nav on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isMobileNavOpen) {
        setIsMobileNavOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileNavOpen]);

  // Rail controls
  const setRailCollapsed = useCallback((collapsed: boolean) => {
    setIsRailCollapsed(collapsed);
  }, []);

  const toggleRailCollapsed = useCallback(() => {
    setIsRailCollapsed((prev) => !prev);
  }, []);

  // Mobile nav controls
  const openMobileNav = useCallback(() => setIsMobileNavOpen(true), []);
  const closeMobileNav = useCallback(() => setIsMobileNavOpen(false), []);

  const value: LayoutContextValue = {
    isRailCollapsed,
    setRailCollapsed,
    toggleRailCollapsed,
    isMobileNavOpen,
    setMobileNavOpen: setIsMobileNavOpen,
    openMobileNav,
    closeMobileNav,
    activeModule,
    setActiveModule,
  };

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export function useLayout(): LayoutContextValue {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLayoutOptional(): LayoutContextValue | null {
  return useContext(LayoutContext);
}

export { LayoutContext };
export default LayoutProvider;
